import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export type Breadcrumb = { label: string; to?: string };

/** Header with breadcrumbs and title, then the form. */
export function RecordFormPageLayout({
  breadcrumbs,
  title,
  subtitle,
  children,
}: {
  breadcrumbs: Breadcrumb[];
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <div className="min-h-full bg-slate-50">
      <div className="border-b bg-white">
        <div className="mx-auto max-w-[1100px] px-6 py-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500" aria-label="Breadcrumb">
            {breadcrumbs.map(({ label, to }, index) => (
              <span key={`${index}-${label}`} className="flex items-center gap-1.5">
                {index > 0 && <ChevronRight className="h-4 w-4 text-gray-300" />}
                {to ? (
                  <Link to={to} className="hover:text-gray-900 hover:underline">{label}</Link>
                ) : (
                  <span className="font-medium text-gray-900">{label}</span>
                )}
              </span>
            ))}
          </nav>
          <h1 className="mt-3 text-2xl font-bold tracking-tight text-gray-900" data-testid="crm-form-title">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
      </div>
      <div className="mx-auto max-w-[1100px] px-6 py-6">{children}</div>
    </div>
  );
}

export function RecordFormSkeleton() {
  return (
    <div className="space-y-6" data-testid="crm-form-skeleton">
      <Skeleton className="h-56 rounded-xl" />
      <Skeleton className="h-40 rounded-xl" />
      <Skeleton className="h-32 rounded-xl" />
    </div>
  );
}
